/**
 * @fileoverview Promotional Cover Screen Component for 'Cronómetro' (ADSL Tile ID 50) on stadium LED screens (/screen).
 * Mirrors the exact DOM structure, CSS classes, and layout of Screen Welcome.js:
 *  - Screen__Welcome__Background with Barrio 9 background (/img/games/cronometer/background-1.png)
 *  - Screen__Welcome__Container Container-FullHeight
 *    - Left: Screen__Welcome__Container__QR with pre-fabricated QR image (/img/page/qr/cronometer-qr-fondo-gris-con-parametros.png)
 *    - Right: Screen__Welcome__Container__Info with game logo (/img/games/cronometer/logo-cronometro.png) and participation instructions
 *  - Screen__Welcome__Footer with credits.
 * 
 * STRICT REQUIREMENT: No textual access URLs are rendered; only the pre-fabricated QR code image is displayed.
 * 
 * @module components/Screen/Sections/Cronometer/CronometerCover
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GetPublicConfig } from '../../../../utilities/DL/Cronometer';

const DEFAULT_COVER_BACKGROUND = '/img/games/cronometer/background-1.png';
const DEFAULT_GAME_LOGO = '/img/games/cronometer/logo-cronometro.png';
const DEFAULT_QR_CODE = '/img/page/qr/cronometer-qr-fondo-gris-con-parametros.png';

/**
 * Resolves media paths to fully qualified URLs when needed.
 * 
 * @param {string|null} mediaPath - Relative or absolute path to asset.
 * @param {string} fallbackPath - Default fallback image.
 * @returns {string} Fully resolved image URL.
 */
const resolveMediaUrl = (mediaPath, fallbackPath) => {
    if (!mediaPath) return fallbackPath;
    if (
        mediaPath.startsWith('http://') ||
        mediaPath.startsWith('https://') ||
        mediaPath.startsWith('blob:') ||
        mediaPath.startsWith('data:')
    ) {
        return mediaPath;
    }
    const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
    const cleanPath = mediaPath.startsWith('/') ? mediaPath : `/${mediaPath}`;
    return apiBase ? `${apiBase}${cleanPath}` : cleanPath;
};

/**
 * CronometerCover Component (/screen - Tile ID 50)
 * 
 * @function CronometerCover
 * @param {Object} props - Component properties.
 * @param {Object} [props.payload] - WebSocket payload containing optional theme configuration.
 * @returns {React.ReactElement} The rendered stadium cover view.
 */
export const CronometerCover = ({ payload }) => {
    const [themeData, setThemeData] = useState(null);

    // Initial configuration fetch if theme is not provided in incoming WebSocket payload
    const fetchThemeConfiguration = useCallback(async () => {
        try {
            const response = await GetPublicConfig();
            if (response && response.success && response.data) {
                if (response.data.theme) {
                    setThemeData(response.data.theme);
                } else if (response.data.activeTheme) {
                    setThemeData(response.data.activeTheme);
                }
            }
        } catch (error) {
            console.error('Error fetching Cronometer theme configuration:', error);
        }
    }, []);

    useEffect(() => {
        const rootEl = document.getElementById("root");
        if (rootEl) {
            rootEl.classList.add("BodyComplete");
        }

        if (payload?.theme || payload?.currentTheme || payload?.activeTheme) {
            setThemeData(payload.theme || payload.currentTheme || payload.activeTheme);
        } else {
            fetchThemeConfiguration();
        }
    }, [payload, fetchThemeConfiguration]);

    const backgroundUrl = resolveMediaUrl(
        themeData?.backgroundUrl,
        DEFAULT_COVER_BACKGROUND
    );
    const gameLogoUrl = resolveMediaUrl(
        themeData?.logoUrl,
        DEFAULT_GAME_LOGO
    );
    const qrCodeUrl = resolveMediaUrl(
        themeData?.qrCodeUrl,
        DEFAULT_QR_CODE
    );

    return (
        <>
            {/* Encabezado / Capa de fondo panorámico Barrio 9 */}
            <div className="Screen__Welcome__Background">
                <img
                    src={backgroundUrl}
                    className="Screen__Welcome__Background__Image"
                    alt="Fondo Cronómetro"
                    onError={(e) => {
                        if (e.currentTarget.src !== DEFAULT_COVER_BACKGROUND) {
                            e.currentTarget.src = DEFAULT_COVER_BACKGROUND;
                        }
                    }}
                />
            </div>

            {/* Contenido principal idéntico a Welcome.js (50/50 limpio sin URL de texto) */}
            <section className="Screen__Welcome__Container Container-FullHeight">
                <div className="Screen__Welcome__Container__QR">
                    <img
                        src={qrCodeUrl}
                        className="Screen__Welcome__Container__QR__Image"
                        alt="Código QR Cronómetro"
                        onError={(e) => {
                            if (e.currentTarget.src !== DEFAULT_QR_CODE) {
                                e.currentTarget.src = DEFAULT_QR_CODE;
                            }
                        }}
                    />
                </div>

                <div className="Screen__Welcome__Container__Info">
                    <img
                        src={gameLogoUrl}
                        className="Screen__Welcome__Container__Info__Image"
                        style={{ maxWidth: '350px' }}
                        alt="Logotipo Oficial Cronómetro"
                        onError={(e) => {
                            if (e.currentTarget.src !== DEFAULT_GAME_LOGO) {
                                e.currentTarget.src = DEFAULT_GAME_LOGO;
                            }
                        }}
                    />
                    <p className="Screen__Welcome__Container__Text">Escanea el código</p>
                    <p className="Screen__Welcome__Container__Text">y participa en la dinámica</p>
                </div>
            </section>

            {/* Pie de página institucional */}
            <footer className="Screen__Welcome__Footer">
                <p className="text-center FooterCredits">Hecho con ♥ por Interaktia | 2026</p>
            </footer>
        </>
    );
};

CronometerCover.propTypes = {
    payload: PropTypes.shape({
        type: PropTypes.string,
        mode: PropTypes.string,
        theme: PropTypes.object,
        currentTheme: PropTypes.object,
        activeTheme: PropTypes.object,
        data: PropTypes.object,
        payload: PropTypes.object,
        state: PropTypes.object
    })
};

CronometerCover.defaultProps = {
    payload: null
};

export default CronometerCover;
